import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFacebook, faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import {
  faCircleCheck,
  faCircleInfo,
  faGear,
  faMessage,
  faPlugCircleBolt,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { MetaIntegrationPopup } from "../MyCompany/MetaIntegrationPopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

export const WhatsAppOfficialPage: React.FC = () => {
  const { t } = useTranslation("pages")

  const [isMetaOpen, setMetaOpen] = useState(false)

  useQuickActionPopup("whatsapp-official-meta", () => setMetaOpen(true))

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-4 md:p-2">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="p3">
          <div className="mb-3 mt-3 flex flex-row items-start justify-between p-2">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                <FontAwesomeIcon icon={faFacebook} className="text-sm" />
              </span>

              {t("label_whatsapp_official_title")}
            </h2>

            <PopupHelpButton
              variant="global"
              helpKey="pages.whatsapp-official"
              title={t("label_whatsapp_official_title")}
              description={t("label_whatsapp_official_subtitle")}
              bullets={[
                t("label_whatsapp_official_tip_credentials"),
                t("label_whatsapp_official_tip_templates"),
                t("label_whatsapp_official_tip_cost"),
              ]}
              steps={[
                t("label_whatsapp_official_help_step_1"),
                t("label_whatsapp_official_help_step_2"),
                t("label_whatsapp_official_help_step_3"),
                t("label_whatsapp_official_help_step_4"),
                t("label_whatsapp_official_help_step_5"),
              ]}
            />
          </div>

          <div className="mt-3 rounded-lg border-2 border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 shadow-sm">
            <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 sm:mt-0.5">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>

              <div>
                <p className="sm:mt-2">{t("label_whatsapp_official_auth_description")}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-white px-3 py-1 text-xs font-bold text-blue-800">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    {t("label_whatsapp_official_meta_verified")}
                  </span>

                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                    <FontAwesomeIcon icon={faWhatsapp} />
                    {t("label_whatsapp_official_cloud_api")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 shadow-sm">
            <div className="flex flex-col items-center gap-3 text-justify sm:flex-row sm:items-start sm:text-left">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 sm:mt-0.5">
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>

              <div>
                <p className="font-semibold">{t("label_whatsapp_official_cost_title")}</p>

                <p className="mt-1">{t("label_whatsapp_official_cost_description")}</p>
              </div>
            </div>
          </div>

          <div className="mb-3 mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                <FontAwesomeIcon icon={faPlugCircleBolt} />
              </span>

              <p className="mt-2 text-sm font-bold text-slate-800">{t("label_whatsapp_official_card_auth_title")}</p>

              <p className="mt-1 text-xs text-slate-600">{t("label_whatsapp_official_card_auth_description")}</p>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <FontAwesomeIcon icon={faMessage} />
              </span>

              <p className="mt-2 text-sm font-bold text-slate-800">
                {t("label_whatsapp_official_card_templates_title")}
              </p>

              <p className="mt-1 text-xs text-slate-600">{t("label_whatsapp_official_card_templates_description")}</p>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <FontAwesomeIcon icon={faGear} />
              </span>

              <p className="mt-2 text-sm font-bold text-slate-800">{t("label_whatsapp_official_card_actions_title")}</p>

              <p className="mt-1 text-xs text-slate-600">{t("label_whatsapp_official_card_actions_description")}</p>
            </section>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMetaOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-gradient-to-r from-blue-600 to-sky-600 px-3 py-2 text-sm font-bold text-white shadow-md transition hover:brightness-105"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppOfficial.whatsAppOfficialPage.button.setMetaOpenTrue"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-700 text-white">
                <FontAwesomeIcon icon={faGear} />
              </span>

              <span>{t("label_whatsapp_official_open_settings")}</span>
            </button>
          </div>
        </div>
      </Animate>

      {isMetaOpen && <MetaIntegrationPopup onClose={() => setMetaOpen(false)} />}
    </div>
  )
}
